import React, { useEffect, useState } from 'react';
import { analyticsService, DashboardAnalytics, EnhancedAnalytics } from '../../services/analyticsService';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Skeleton,
  Alert,
  Chip
} from '@mui/material';
import { usePermissions } from '@/hooks/usePermissions';

interface SummaryCardProps {
  title: string;
  count: number;
  icon: string;
  color: string;
  onClick?: () => void;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, count, icon, color, onClick }) => (
  <Card
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      borderLeft: `4px solid ${color}`,
      '&:hover': onClick ? {
        boxShadow: 3,
        transform: 'translateY(-2px)',
        transition: 'all 0.2s ease'
      } : {}
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {(count ?? 0).toLocaleString()}
          </Typography>
        </Box>
        <Typography variant="h3" sx={{ opacity: 0.7 }}>
          {icon}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

interface DashboardSummaryProps {
  onCategoryClick?: (category: string) => void;
}

const DashboardSummary: React.FC<DashboardSummaryProps> = ({ onCategoryClick }) => {
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isSuperAdmin } = usePermissions();

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const data = await analyticsService.getDashboardAnalytics();
        setAnalytics(data);
        setError(null);
      } catch (err) {
        setError('Failed to load dashboard analytics');
        console.error('Dashboard analytics error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[...Array(5)].map((_, i) => (
            <Grid item xs={12} sm={6} md={2.4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error || !analytics) {
    return (
      <Alert severity="error" sx={{ mb: 4 }}>
        {error || 'Failed to load analytics'}
      </Alert>
    );
  }

  const formatCurrency = (amount: number | undefined | null) => {
    const value = amount || 0;
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Calculate vehicle rentals revenue - use backend's vehicle-rental category
  const getVehicleRentalsRevenue = () => {
    // Backend provides vehicle-rental as a calculated field
    return analytics?.revenue?.byCategory?.['vehicle-rental'] ?? 0;
  };

  // Get revenue with fallback to 0 if no data
  const getRevenueWithFallback = (category: keyof DashboardAnalytics['revenue']['byCategory']) => {
    return analytics?.revenue?.byCategory?.[category] ?? 0;
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Main Category Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <SummaryCard
            title="Total Bookings"
            count={analytics?.categories?.all ?? 0}
            icon="📊"
            color="#3b82f6"
            onClick={() => onCategoryClick?.('all')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <SummaryCard
            title="Self-Drive"
            count={analytics?.categories?.['self-drive'] ?? 0}
            icon="🚗"
            color="#10b981"
            onClick={() => onCategoryClick?.('self-drive')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <SummaryCard
            title="With Driver"
            count={analytics?.categories?.['with-driver'] ?? 0}
            icon="🤵"
            color="#8b5cf6"
            onClick={() => onCategoryClick?.('with-driver')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <SummaryCard
            title="Driver Hire"
            count={analytics?.categories?.['driver-hire'] ?? 0}
            icon="👨‍✈️"
            color="#f59e0b"
            onClick={() => onCategoryClick?.('driver-hire')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <SummaryCard
            title="Yacht Bookings"
            count={analytics?.categories?.['yacht-charter'] ?? 0}
            icon="🛥️"
            color="#ec4899"
            onClick={() => onCategoryClick?.('yacht-charter')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <SummaryCard
            title="Hybrid Bookings"
            count={analytics?.categories?.hybrid ?? 0}
            icon="🔄"
            color="#6366f1"
            onClick={() => onCategoryClick?.('hybrid')}
          />
        </Grid>
      </Grid>

      {/* Revenue and Status Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {isSuperAdmin() && (
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                borderLeft: `4px solid #059669`,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Total Revenue
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {formatCurrency(analytics?.revenue?.total ?? 0)}
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ opacity: 0.7 }}>
                    💰
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={isSuperAdmin() ? 4 : 6}>
          <SummaryCard
            title="Instant Bookings"
            count={analytics?.bookingTypes?.instant ?? 0}
            icon="⚡"
            color="#eab308"
            onClick={() => onCategoryClick?.('instant')}
          />
        </Grid>

        <Grid item xs={12} md={isSuperAdmin() ? 4 : 6}>
          <SummaryCard
            title="Pending Approval"
            count={analytics?.bookingTypes?.['approval-required'] ?? 0}
            icon="🔍"
            color="#ef4444"
            onClick={() => onCategoryClick?.('pending')}
          />
        </Grid>
      </Grid>

      {/* Revenue Breakdown - Only visible to superadmins */}
      {isSuperAdmin() && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Revenue Breakdown
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Vehicle Rentals
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981' }}>
                    {formatCurrency(getVehicleRentalsRevenue())}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Driver Services
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                    {formatCurrency(getRevenueWithFallback('driver-hire'))}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Yacht Charters
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#ec4899' }}>
                    {formatCurrency(getRevenueWithFallback('yacht-charter'))}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Hybrid Bookings
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#6366f1' }}>
                    {formatCurrency(getRevenueWithFallback('hybrid'))}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default DashboardSummary;